import { ThemeType } from "@microsoft/shifts-ui-core";
import * as microsoftTeams from "@microsoft/teams-js";
import { loadFeedbackAndBootstrap } from "@microsoft/shifts-entrypoints-teams-feedback";
import {
    FeedbackHostPlatformType,
    FeedbackUiType,
    InAppFeedbackQuestionUiType,
    InAppFeedbackQuestionUiBehaviour,
    InAppFeedbackScenarioType,
    FeedbackType,
    FeedbackAuthenticationType,
    FeedbackPolicyValue,
    IFeedbackConfig,
    IFeedbackInitOptions,
    IThemeOptions,
    openFeedback
} from "@ms/inapp-feedback-loader";
import { MicrosoftTeamsClient, MicrosoftTeams_WEB_OCV_APPID, MicrosotTeams_WINDOWS_OCV_APPID } from "../../StaffHubConstants";
import { InstrumentationService } from "sh-services";
import StringsStore from "sh-strings/store";

/**
 * Opens a feedback modal after a successful Assign open shifts job (5-star rating).
 *
 * @param feedbackElementId - The DOM element ID where feedback should be rendered
 * @param currentUser - Current user object with email, tenantId, userId
 * @param hostPlatform - Host platform type (web, desktop, etc.)
 * @param theme - Current theme (Dark, Light, HighContrast)
 * @param onSubmitCallback - Optional callback to execute when feedback is submitted
 * @param onDismissCallback - Optional callback to execute when feedback is dismissed without submitting
 * @param isFeedbackOpenRef - Optional ref to track feedback open state
 */
export async function openAssignOpenShiftsFeedback(
    feedbackElementId: string,
    currentUser: any,
    hostPlatform: any,
    theme: ThemeType,
    onSubmitCallback?: () => void,
    onDismissCallback?: () => void,
    isFeedbackOpenRef?: React.MutableRefObject<boolean>
): Promise<void> {
    const _strings = StringsStore().registeredStringModules.get("schedulePage")?.strings;

    if (isFeedbackOpenRef) {
        isFeedbackOpenRef.current = true;
    }

    if (!currentUser) {
        if (isFeedbackOpenRef) {
            isFeedbackOpenRef.current = false;
        }
        return;
    }

    const inAppFeedbackElement = document.getElementById(feedbackElementId);

    if (!inAppFeedbackElement) {
        console.error("Feedback element not found in DOM");
        if (isFeedbackOpenRef) {
            isFeedbackOpenRef.current = false;
        }
        return;
    }

    let feedbackHostPlatformType: FeedbackHostPlatformType;
    let appId: number;

    if (hostPlatform == microsoftTeams.HostClientType.web) {
        feedbackHostPlatformType = FeedbackHostPlatformType.Web;
        appId = MicrosoftTeams_WEB_OCV_APPID;
    } else if (hostPlatform == microsoftTeams.HostClientType.desktop) {
        feedbackHostPlatformType = FeedbackHostPlatformType.Windows;
        appId = MicrosotTeams_WINDOWS_OCV_APPID;
    }

    try {
        await loadFeedbackAndBootstrap({ inAppFeedbackElement });

        let themeOptions: IThemeOptions;
        switch (theme) {
            case ThemeType.Dark:
                themeOptions = { baseTheme: "TeamsDarkV2" };
                break;
            case ThemeType.Light:
                themeOptions = { baseTheme: "TeamsLightV2" };
                break;
            case ThemeType.HighContrast:
                themeOptions = { baseTheme: "TeamsHighContrastV2" };
                break;
            default:
                themeOptions = { baseTheme: "TeamsLightV2" };
                break;
        }

        const feedbackConfig: IFeedbackConfig = {
            initialFeedbackType: FeedbackType.Unclassified,
            hostPlatform: feedbackHostPlatformType,
            feedbackUiType: FeedbackUiType.Modal,
            isDisplayed: true,
            isEmailCollectionEnabled: false,
            isScreenshotEnabled: false,
            isScreenRecordingEnabled: false,
            isFileUploadEnabled: false,
            isThankYouPageDisabled: false,
            privacyUrl: "https://go.microsoft.com/fwlink/?LinkId=521839",
            complianceChecks: {
                policyAllowFeedback: FeedbackPolicyValue.Enabled,
                policyAllowScreenshot: FeedbackPolicyValue.Enabled,
                policyAllowContact: FeedbackPolicyValue.Disabled
            },
            scenarioConfig: {
                isScenarioEnabled: true,
                scenarioType: InAppFeedbackScenarioType.Custom,
                questionDetails: {
                    questionUiType: InAppFeedbackQuestionUiType.Rating,
                    questionInstruction: {
                        displayedStringInEnglish: _strings.get("assignOpenShiftsFeedbackRatingQuestion"),
                        displayedString: _strings.get("assignOpenShiftsFeedbackRatingQuestion")
                    },
                    questionOptions: [
                        {
                            displayedStringInEnglish: _strings.get("assignOpenShiftsFeedbackRating1Star"),
                            displayedString: _strings.get("assignOpenShiftsFeedbackRating1Star")
                        },
                        {
                            displayedStringInEnglish: _strings.get("assignOpenShiftsFeedbackRating2Stars"),
                            displayedString: _strings.get("assignOpenShiftsFeedbackRating2Stars")
                        },
                        {
                            displayedStringInEnglish: _strings.get("assignOpenShiftsFeedbackRating3Stars"),
                            displayedString: _strings.get("assignOpenShiftsFeedbackRating3Stars")
                        },
                        {
                            displayedStringInEnglish: _strings.get("assignOpenShiftsFeedbackRating4Stars"),
                            displayedString: _strings.get("assignOpenShiftsFeedbackRating4Stars")
                        },
                        {
                            displayedStringInEnglish: _strings.get("assignOpenShiftsFeedbackRating5Stars"),
                            displayedString: _strings.get("assignOpenShiftsFeedbackRating5Stars")
                        }
                    ],
                    questionUiBehaviour: [InAppFeedbackQuestionUiBehaviour.CommentNotRequired]
                }
            }
        };

        const options: IFeedbackInitOptions = {
            appId: appId,
            authenticationType: FeedbackAuthenticationType.Unauthenticated,
            callbackFunctions: {
                onError: (errorMessage?: string) => {
                    InstrumentationService.trackException(
                        new Error(errorMessage),
                        "AssignOpenShiftsFeedbackUtil.FeedbackCallback.onError"
                    );
                },
                onSuccess: () => {
                    if (onSubmitCallback) {
                        onSubmitCallback();
                    }
                },
                onDismiss: (isFeedbackSent?: boolean) => {
                    if (isFeedbackOpenRef) {
                        isFeedbackOpenRef.current = false;
                    }
                    if (!isFeedbackSent && onDismissCallback) {
                        onDismissCallback();
                    }
                }
            },
            clientName: MicrosoftTeamsClient,
            feedbackConfig: feedbackConfig,
            telemetry: {
                featureArea: "Shifts",
                featureName: "Assign Open Shifts"
            },
            isProduction: true,
            themeOptions: themeOptions,
            tenantId: currentUser.tenantId,
            userId: currentUser.userId
        };

        openFeedback(options);
    } catch (error) {
        console.error("Error opening Assign open shifts feedback:", error);
        InstrumentationService.trackException(
            error,
            "AssignOpenShiftsFeedbackUtil.openAssignOpenShiftsFeedback"
        );
        if (isFeedbackOpenRef) {
            isFeedbackOpenRef.current = false;
        }
    }
}

/**
 * Opens a feedback modal after the user cancels an Assign open shifts job (multi-select reasons).
 *
 * @param feedbackElementId - The DOM element ID where feedback should be rendered
 * @param currentUser - Current user object with email, tenantId, userId
 * @param hostPlatform - Host platform type (web, desktop, etc.)
 * @param theme - Current theme (Dark, Light, HighContrast)
 * @param onSubmitCallback - Optional callback to execute when feedback is submitted
 * @param onDismissCallback - Optional callback to execute when feedback is dismissed without submitting
 * @param isFeedbackOpenRef - Optional ref to track feedback open state
 */
export async function openAssignOpenShiftsCancelFeedback(
    feedbackElementId: string,
    currentUser: any,
    hostPlatform: any,
    theme: ThemeType,
    onSubmitCallback?: () => void,
    onDismissCallback?: () => void,
    isFeedbackOpenRef?: React.MutableRefObject<boolean>
): Promise<void> {
    const _strings = StringsStore().registeredStringModules.get("schedulePage")?.strings;

    if (isFeedbackOpenRef) {
        isFeedbackOpenRef.current = true;
    }

    if (!currentUser) {
        if (isFeedbackOpenRef) {
            isFeedbackOpenRef.current = false;
        }
        return;
    }

    const inAppFeedbackElement = document.getElementById(feedbackElementId);

    if (!inAppFeedbackElement) {
        console.error("Feedback element not found in DOM");
        if (isFeedbackOpenRef) {
            isFeedbackOpenRef.current = false;
        }
        return;
    }

    let feedbackHostPlatformType: FeedbackHostPlatformType;
    let appId: number;

    if (hostPlatform == microsoftTeams.HostClientType.web) {
        feedbackHostPlatformType = FeedbackHostPlatformType.Web;
        appId = MicrosoftTeams_WEB_OCV_APPID;
    } else if (hostPlatform == microsoftTeams.HostClientType.desktop) {
        feedbackHostPlatformType = FeedbackHostPlatformType.Windows;
        appId = MicrosotTeams_WINDOWS_OCV_APPID;
    }

    try {
        await loadFeedbackAndBootstrap({ inAppFeedbackElement });

        let themeOptions: IThemeOptions;
        switch (theme) {
            case ThemeType.Dark:
                themeOptions = { baseTheme: "TeamsDarkV2" };
                break;
            case ThemeType.Light:
                themeOptions = { baseTheme: "TeamsLightV2" };
                break;
            case ThemeType.HighContrast:
                themeOptions = { baseTheme: "TeamsHighContrastV2" };
                break;
            default:
                themeOptions = { baseTheme: "TeamsLightV2" };
                break;
        }

        const feedbackConfig: IFeedbackConfig = {
            initialFeedbackType: FeedbackType.Unclassified,
            hostPlatform: feedbackHostPlatformType,
            feedbackUiType: FeedbackUiType.Modal,
            isDisplayed: true,
            isEmailCollectionEnabled: false,
            isScreenshotEnabled: false,
            isScreenRecordingEnabled: false,
            isFileUploadEnabled: false,
            isThankYouPageDisabled: false,
            privacyUrl: "https://go.microsoft.com/fwlink/?LinkId=521839",
            complianceChecks: {
                policyAllowFeedback: FeedbackPolicyValue.Enabled,
                policyAllowScreenshot: FeedbackPolicyValue.Enabled,
                policyAllowContact: FeedbackPolicyValue.Disabled
            },
            scenarioConfig: {
                isScenarioEnabled: true,
                scenarioType: InAppFeedbackScenarioType.Custom,
                questionDetails: {
                    questionUiType: InAppFeedbackQuestionUiType.MultiSelect,
                    questionInstruction: {
                        displayedStringInEnglish: _strings.get("assignOpenShiftsFeedbackCancelQuestion"),
                        displayedString: _strings.get("assignOpenShiftsFeedbackCancelQuestion")
                    },
                    questionOptions: [
                        {
                            displayedStringInEnglish: _strings.get("assignOpenShiftsFeedbackCancelReason1"),
                            displayedString: _strings.get("assignOpenShiftsFeedbackCancelReason1")
                        },
                        {
                            displayedStringInEnglish: _strings.get("assignOpenShiftsFeedbackCancelReason2"),
                            displayedString: _strings.get("assignOpenShiftsFeedbackCancelReason2")
                        },
                        {
                            displayedStringInEnglish: _strings.get("assignOpenShiftsFeedbackCancelReason3"),
                            displayedString: _strings.get("assignOpenShiftsFeedbackCancelReason3")
                        },
                        {
                            displayedStringInEnglish: _strings.get("assignOpenShiftsFeedbackCancelReasonOther"),
                            displayedString: _strings.get("assignOpenShiftsFeedbackCancelReasonOther")
                        }
                    ],
                    questionUiBehaviour: [InAppFeedbackQuestionUiBehaviour.CommentNotRequired]
                }
            }
        };

        const options: IFeedbackInitOptions = {
            appId: appId,
            authenticationType: FeedbackAuthenticationType.Unauthenticated,
            callbackFunctions: {
                onError: (errorMessage?: string) => {
                    InstrumentationService.trackException(
                        new Error(errorMessage),
                        "AssignOpenShiftsFeedbackUtil.CancelFeedback.onError"
                    );
                },
                onSuccess: () => {
                    if (onSubmitCallback) {
                        onSubmitCallback();
                    }
                },
                onDismiss: (isFeedbackSent?: boolean) => {
                    if (isFeedbackOpenRef) {
                        isFeedbackOpenRef.current = false;
                    }
                    if (!isFeedbackSent && onDismissCallback) {
                        onDismissCallback();
                    }
                }
            },
            clientName: MicrosoftTeamsClient,
            feedbackConfig: feedbackConfig,
            telemetry: {
                featureArea: "Shifts",
                featureName: "Assign Open Shifts"
            },
            isProduction: true,
            themeOptions: themeOptions,
            tenantId: currentUser.tenantId,
            userId: currentUser.userId
        };

        openFeedback(options);
    } catch (error) {
        console.error("Error opening Assign open shifts cancel feedback:", error);
        InstrumentationService.trackException(
            error,
            "AssignOpenShiftsFeedbackUtil.openAssignOpenShiftsCancelFeedback"
        );
        if (isFeedbackOpenRef) {
            isFeedbackOpenRef.current = false;
        }
    }
}
